import { z } from 'zod';
import { COMPLIANCE_FIXES, ComplianceFixId, normalTitle, } from './compliance-fixes.js';
import { ServerId } from './basics.js';
/**
 * Server tasks: the compliance fixes Bartizo makes, as things an administrator
 * manages per server — is it done, is it running, does it wait for a reboot, or does
 * a person have to do something.
 *
 * A task is one or more catalogue fixes that do the same work (the two boot checks
 * are one change to one line). Its state is **read from what is true**, not from a
 * flag Bartizo keeps: whether the benchmark's check now passes on the server, what
 * the last command for it came to, and whether the server is waiting for a reboot.
 * So it cannot say "done" about something the next scan disagrees with.
 */
/** The labels of the tasks whose fixes are shared; any other task takes its check's title. */
const LABELS = {
    'audit-boot-before-auditd': 'Auditing: boot options (audit=1, backlog 8192)',
    'audit-chacl-collected': 'Auditing: install auditd and the baseline rules',
    'cron-crontab-permissions': 'Cron files: root-only permissions',
};
const sentence = (text) => text.charAt(0).toUpperCase() + text.slice(1);
export const HARDENING_TASKS = (() => {
    const groups = new Map();
    for (const fix of COMPLIANCE_FIXES) {
        const id = ComplianceFixId.parse(fix.id);
        const seen = groups.get(fix.commands);
        if (seen === undefined) {
            groups.set(fix.commands, {
                id,
                label: LABELS[fix.id] ?? sentence(fix.title),
                does: fix.does,
                restart: fix.restart,
                fixIds: [id],
                titles: [fix.title],
            });
        }
        else {
            seen.fixIds.push(id);
            seen.titles.push(fix.title);
        }
    }
    return [...groups.values()];
})();
export const hardeningTaskById = (id) => HARDENING_TASKS.find((task) => task.id === id);
export const HardeningStatus = z.enum([
    'done',
    'running',
    'checking',
    'needs_reboot',
    'needs_you',
    'not_applied',
    'unknown',
]);
/** What each state tells the person, in the words the page uses. */
export const HARDENING_STATUS_LABEL = {
    done: 'Done',
    running: 'Running',
    checking: 'Applied, waiting for the next scan',
    needs_reboot: 'Needs a reboot',
    needs_you: 'Needs you',
    not_applied: 'Not applied',
    unknown: 'Not assessed yet',
};
/** Wazuh assesses a server about every 12 hours; a little longer than that is a real failure. */
export const HARDENING_RESCAN_GRACE_MS = 13 * 3_600_000;
const FAILED_COMMAND = new Set(['failed', 'rejected', 'expired', 'cancelled', 'skipped']);
export function hardeningStatus(input) {
    const { last } = input;
    if (last !== undefined && (last.status === 'pending' || last.status === 'sent')) {
        return { status: 'running' };
    }
    const failing = input.checks.includes('failed');
    const judged = failing || input.checks.includes('passed');
    if (!judged)
        return { status: 'unknown' };
    if (!failing)
        return { status: 'done' };
    if (last === undefined)
        return { status: 'not_applied' };
    const said = last.detail ?? undefined;
    if (FAILED_COMMAND.has(last.status)) {
        const why = last.status === 'expired'
            ? 'The server was offline when it was sent, so it never ran.'
            : (said ?? `The last attempt ${last.status}.`);
        return { status: 'needs_you', detail: why };
    }
    // The helper's own words when it declined to act, e.g. no /etc/default/grub here.
    if (said?.includes('NOT DONE') === true)
        return { status: 'needs_you', detail: said };
    if (input.restart === 'reboot' && input.restartPending)
        return { status: 'needs_reboot' };
    if (input.now.getTime() - last.at.getTime() > HARDENING_RESCAN_GRACE_MS) {
        return {
            status: 'needs_you',
            detail: 'It was applied and a new scan has run, but the check still fails here.',
        };
    }
    return { status: 'checking' };
}
export const HardeningServer = z.object({
    id: ServerId,
    name: z.string(),
    status: HardeningStatus,
    detail: z.string().optional(),
    /** When the last command for this task finished (or was queued), if there was one. */
    lastTriedAt: z.string().optional(),
});
export const HardeningTaskView = z.object({
    id: ComplianceFixId,
    label: z.string(),
    does: z.string(),
    restart: z.enum(['none', 'reboot']),
    /** An administrator asked for it to be applied by itself to servers that never had it tried. */
    auto: z.object({ enabled: z.boolean(), byEmail: z.string().nullable() }),
    counts: z.record(HardeningStatus, z.number().int().nonnegative()),
    servers: z.array(HardeningServer),
});
export const HardeningView = z.object({ tasks: z.array(HardeningTaskView) });
/** Where a task's rows sort: what needs a person first. */
export const HARDENING_STATUS_ORDER = [
    'needs_you',
    'needs_reboot',
    'running',
    'not_applied',
    'checking',
    'unknown',
    'done',
];
export { normalTitle };
export const SetHardeningAuto = z.object({ enabled: z.boolean() }).strict();
/** At most this many servers are queued in one pass, so switching a task on never changes a whole fleet at once. */
export const HARDENING_AUTO_BATCH = 5;
//# sourceMappingURL=hardening-tasks.js.map